import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const STEP_ICONS = {
  choose: FiIcons.FiMousePointer,
  install: FiIcons.FiDownload,
  activate: FiIcons.FiKey,
  use: FiIcons.FiUnlock,
};

const HowItWorks = () => {
  const { language } = useLanguage();
  const text = t[language].howItWorks;

  return (
    <section id="how-it-works" className="py-16 sm:py-24 bg-[#0a0a0f] relative overflow-hidden border-t border-white/5">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {text.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-xl text-gray-400"
          >
            {text.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative max-w-6xl mx-auto">
          <div className="hidden lg:block absolute top-12 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent z-0" />

          {text.steps.map((step, idx) => {
            const iconKey = Object.keys(STEP_ICONS)[idx] || 'use';
            const icon = STEP_ICONS[iconKey];
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
                className="relative z-10 flex flex-col items-center text-center group"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#1a1a24] border-2 border-orange-500/20 flex flex-col items-center justify-center mb-6 relative overflow-hidden group-hover:border-orange-500/60 transition-colors">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <SafeIcon icon={icon} className="text-orange-400 text-2xl sm:text-3xl mb-1 relative z-10" />
                  <span className="text-xs font-black text-orange-400/40 relative z-10 uppercase tracking-wide">
                    {text.stepLabel} {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;